import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { API_BASE } from "../lib/api";
import { EASE, scrollToId } from "./Weave";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-2 -mb-2">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function TriangleArt() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 6]);

  return (
    <div ref={ref} className="relative h-full w-full">
      <motion.div className="flex h-full w-full items-center justify-center" style={{ y, rotate }}>
        <motion.img
          src="/triangles.webp"
          alt=""
          aria-hidden="true"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1, y: [0, -14, 0] }}
          transition={{
            opacity: { duration: 1.2, delay: 0.4 },
            scale: { duration: 1.2, delay: 0.4, ease: EASE },
            y: { duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1.6 },
          }}
          className="h-full w-full object-contain"
        />
      </motion.div>
    </div>
  );
}

export default function Hero({ onJoin }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    fetch(`${API_BASE}/waitlist/count`)
      .then((r) => r.json())
      .then((d) => setCount(d.count || 0))
      .catch(() => {});
  }, []);

  return (
    <section data-testid="hero-section" className="relative overflow-hidden pt-28 sm:pt-32 lg:pt-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-16 sm:px-6 lg:grid-cols-12 lg:gap-6 lg:px-8 lg:pb-24">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-tealb/40 bg-teallight px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-tealdark"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-tealb animate-pulse-dot" />
            Waitlist now open · April 2027
          </motion.p>

          <h1 className="text-[clamp(3rem,9vw,6.5rem)] font-extrabold leading-[0.98] tracking-tight text-ink">
            <MaskedLine delay={0.45}>Your experience,</MaskedLine>
            <MaskedLine delay={0.6}>
              <span className="relative inline-block text-tealb">
                rewoven.
                <motion.svg
                  viewBox="0 0 240 16"
                  preserveAspectRatio="none"
                  className="absolute -bottom-3 left-0 h-4 w-full"
                  aria-hidden="true"
                >
                  <motion.path
                    d="M5 11 C 70 3, 170 3, 235 9"
                    fill="none"
                    stroke="#E93E36"
                    strokeWidth="6"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 1.35, duration: 0.9, ease: EASE }}
                  />
                </motion.svg>
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
            className="mt-8 max-w-xl text-lg leading-relaxed text-body2 sm:text-xl"
          >
            The Weave Founder Academy is a year-long programme for experienced professionals asking
            what&rsquo;s next — starting with <strong className="font-semibold text-ink">Personal Clarity</strong>:
            ten weeks, delivered online in the early evenings and occasionally at weekends, to explore
            founding with honesty, evidence, and no pressure to have it all figured out.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-join-waitlist"
              onClick={onJoin}
              className="group inline-flex items-center gap-3 rounded-full bg-wred px-8 py-4 text-base font-semibold text-white shadow-[0_10px_30px_-10px_rgba(233,62,54,0.6)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-ink"
            >
              Join the waitlist
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-explore-programme"
              onClick={() => scrollToId("programme")}
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-8 py-4 text-base font-semibold text-ink transition-colors hover:border-ink hover:bg-ink hover:text-paper"
            >
              Explore the programme
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>

          {count > 0 && (
            <motion.p
              data-testid="hero-waitlist-count"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.6 }}
              className="mt-8 text-sm font-medium text-muted2"
            >
              You&rsquo;d be joining <span className="font-bold text-tealdark">{count}</span>{" "}
              {count === 1 ? "founder" : "founders"} already exploring what&rsquo;s next.
            </motion.p>
          )}
        </div>

        <div className="relative h-80 sm:h-96 lg:col-span-5 lg:h-[560px]">
          <TriangleArt />
          <motion.img
            src="/photos/hero-portrait.webp"
            alt="A woman pausing to consider her next chapter"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9, ease: EASE }}
            className="absolute bottom-0 left-1/2 z-10 h-[72%] w-auto max-w-[88%] -translate-x-1/2 rounded-t-[999px] rounded-b-3xl object-cover shadow-[0_30px_60px_-30px_rgba(14,47,50,0.5)] ring-1 ring-ink/10 sm:left-auto sm:right-2 sm:translate-x-0 lg:h-[76%]"
          />
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.1, duration: 0.7, ease: EASE }}
            className="absolute right-0 top-0 z-20 w-max rounded-full border border-paperborder bg-paper/95 px-4 py-2 shadow-lg backdrop-blur sm:right-2 sm:top-4 sm:px-5 sm:py-2.5"
          >
            <span className="flex items-center gap-2.5 text-xs font-semibold text-ink sm:text-sm">
              <span className="h-2 w-2 rounded-full bg-wred animate-pulse-dot" />
              Opening April 2027 · waitlist open
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
